"use client";

import { Copy, Check, Mail, Facebook, Twitter } from "lucide-react";
import { useState } from "react";

type ShareButtonsProps = {
  url: string;
  title: string;
};

const btnClass =
  "inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--color-border)] bg-slate-50 text-[var(--color-primary)] transition-colors hover:border-[var(--color-accent)] hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600";

export function ShareButtons({ url, title }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    const succeed = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    try {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(succeed).catch(() => fallbackCopy());
      } else {
        fallbackCopy();
      }
    } catch {
      fallbackCopy();
    }

    function fallbackCopy() {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        succeed();
      } catch {
        // Cannot copy: icon stays as Copy
      }
    }
  }

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Share this page">
      <a
        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X (Twitter)"
        className={btnClass}
      >
        <Twitter size={16} aria-hidden="true" />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
        className={btnClass}
      >
        <Facebook size={16} aria-hidden="true" />
      </a>
      <a
        href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`}
        aria-label="Share via Email"
        className={btnClass}
      >
        <Mail size={16} aria-hidden="true" />
      </a>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Link copied" : "Copy link"}
        className={btnClass}
      >
        {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}
